import { useEffect, useRef, useState } from 'react';
import { miniApp } from '@telegram-apps/sdk-react';

import {
  containerWrapper,
  photo,
  photoBlock,
  profileContainer
} from './styles.ts';
import PhotosSkeleton from './PhotosSkeleton.tsx';

import { Contact } from '@/api/relation/types.ts';
import { PrivacyLabels, RelationKind, UserStates } from '@/enums/apiEnums.ts';
import RelationController from '@/api/relation/relation.ts';
import { catchErrors } from '@/utils/sentry-monitor.tsx';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import ThreeDots from '@/components/Discovery/Card/Components/ThreeDots.tsx';
import VideoSlide from '@/components/Discovery/Card/Components/VideoSlide.tsx';
import { ImageLoader } from '@/components/Discovery/Card/Components/Content.tsx';
import { NameAgeBlock } from '@/components/UserProfile/NameAgeBlock.tsx';
import { AboutMeBlock } from '@/components/UserProfile/AboutMeBlock.tsx';
import { PersonalInfoBlock } from '@/components/UserProfile/PersonalInfoBlock.tsx';
import { LifestyleBlock } from '@/components/UserProfile/LifestyleBlock.tsx';
import { SendGiftBlock } from '@/components/UserProfile/SendGiftBlock.tsx';
import { UnmatchBlock } from '@/components/UserProfile/UnmatchBlock.tsx';
import { ReportBlock } from '@/components/UserProfile/ReportBlock.tsx';
import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { Profile } from '@/api/login/types.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { useHeaderStore } from '@/store/useHeadersStylesStore.ts';
import UIConfig from '@/UIConfig.ts';
import { LanguagesInfoBlock } from '@/components/UserProfile/LanguagesInfoBlock.tsx';
import { helpers } from '@/helpers.ts';

const transformProfileToRelationContact = (profile: Profile): Contact => {
  const isDistanceHidden = profile.concealed_attributes.includes(
    PrivacyLabels.CONCEALABLE_ATTRIBUTE_LOCATION
  );

  return {
    relation_kind: RelationKind.KIND_UNSPECIFIED,
    distance: isDistanceHidden ? 0 : 0.1,
    profile: {
      hid: profile.hid,
      name: profile.name,
      gender: profile.gender,
      age: profile.age,
      city: profile.city,
      v2photos: profile.v2photos.map((photo) => ({
        variants: photo.variants
      })),
      v2videos: profile.v2videos?.map((video) => ({
        variants: video.variants
      })),
      about_me: profile.about_me,
      online_state: UserStates.ONLINE_STATE_UNSPECIFIED,
      employment_sector: profile.employment_sector,
      height_cm: profile.height_cm,
      body_type: profile.body_type,
      zodiac_sign: profile.zodiac_sign,
      smoking_status: profile.smoking_status,
      alcohol_preference: profile.alcohol_preference,
      spoken_languages: profile.spoken_languages
    }
  };
};

const UserProfilePopup = ({
  handleClose,
  userHid,
  hideBackButton,
  isSelfPreview = false,
  prevHeaderColors = 'dark'
}: {
  handleClose: () => void;
  userHid?: string;
  hideBackButton?: boolean;
  isSelfPreview?: boolean;
  usePrevHeaderColors?: boolean;
  prevHeaderColors?: 'light' | 'dark';
}) => {
  useBackButtonToCloseModal(handleClose, hideBackButton);

  const { setHeaderColors, switchHeaderColors } = useHeaderStore();

  const { aboutMe } = useProfileStore();
  const modalRef = useRef<HTMLDivElement>(null);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFetching, setIsFetching] = useState(true);
  const [relationKind, setRelationKind] = useState<RelationKind | undefined>(
    undefined // undefined relation means self-preview
  );
  const [profileData, setProfileData] = useState<Contact | undefined>();

  useEffect(() => {
    const previousHeaderColor = miniApp.headerColor();

    miniApp.setHeaderColor('#1D1D1D');
    setHeaderColors({
      backgroundColor: UIConfig.Colors.BackgroundDark,
      logoColor: UIConfig.Colors.BackgroundWhite
    });

    const modalElement = modalRef.current;

    const allowScroll = (e: TouchEvent) => {
      e.stopPropagation();
    };

    if (modalElement) {
      modalElement.addEventListener('touchmove', allowScroll, {
        passive: false
      });
    }

    return () => {
      miniApp.setHeaderColor(previousHeaderColor);

      if (prevHeaderColors === 'light') {
        switchHeaderColors('light');
      } else {
        switchHeaderColors('dark');
      }

      if (modalElement) {
        modalElement.removeEventListener('touchmove', allowScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (isSelfPreview && aboutMe?.profile) {
      setProfileData(transformProfileToRelationContact(aboutMe.profile));
      setIsFetching(false);
      return;
    }

    const fetchProfileData = async () => {
      try {
        if (!userHid) return;
        const data = await RelationController.getContact(userHid);
        setProfileData(data.contact);
        setRelationKind(data.contact.relation_kind);
        setIsFetching(false);
      } catch (e) {
        catchErrors(e, true);
      }
    };

    void fetchProfileData();
  }, []);

  const photos = profileData?.profile.v2photos ?? [];
  const videos = profileData?.profile.v2videos ?? [];

  type MediaItem =
    | { type: 'photo'; photo: (typeof photos)[0] }
    | {
        type: 'video';
        videoUrl: string;
        posterUrl?: string;
        thumbhash?: string;
      };

  const mediaItems: MediaItem[] = (() => {
    const items: MediaItem[] = [];
    if (photos.length > 0) {
      items.push({ type: 'photo', photo: photos[0] });
    }
    if (videos.length > 0) {
      const videoUrl = helpers.getVideoUrl(videos[0].variants);
      if (videoUrl) {
        items.push({
          type: 'video',
          videoUrl,
          posterUrl: helpers.getVideoPoster(videos[0].variants),
          thumbhash: helpers.getVideoThumbHash(videos[0].variants)
        });
      }
    }
    for (let i = 1; i < photos.length; i++) {
      items.push({ type: 'photo', photo: photos[i] });
    }
    return items;
  })();

  return (
    <div ref={modalRef} style={profileContainer}>
      <div style={containerWrapper}>
        {isFetching || profileData === undefined ? (
          <PhotosSkeleton />
        ) : (
          <>
            <div style={photoBlock}>
              {mediaItems.length > 0 ? (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 0
                    }}
                    onClick={() =>
                      setCurrentIndex((prev) => (prev + 1) % mediaItems.length)
                    }
                  >
                    {mediaItems[currentIndex]?.type === 'photo' ? (
                      <ImageWithLoader
                        key={helpers.getPrimaryPhoto(
                          mediaItems[currentIndex].photo?.variants
                        )}
                        src={
                          helpers.getPrimaryPhoto(
                            mediaItems[currentIndex].photo?.variants
                          ) ?? ''
                        }
                        thumbhash={helpers.getThumbHash(
                          mediaItems[currentIndex].photo?.variants
                        )}
                        style={profilePhotoStyle}
                        alt="profile"
                      />
                    ) : mediaItems[currentIndex]?.type === 'video' ? (
                      <VideoSlide
                        videoUrl={mediaItems[currentIndex].videoUrl}
                        posterUrl={mediaItems[currentIndex].posterUrl}
                        thumbhash={mediaItems[currentIndex].thumbhash}
                        candidateName={profileData.profile.name}
                        isActive={true}
                        hideBackButton={false}
                      />
                    ) : null}
                  </div>
                  <ThreeDots
                    activeIndex={currentIndex}
                    imagesCount={mediaItems.length}
                    mediaTypes={mediaItems.map((item) => item.type)}
                  />
                </>
              ) : (
                <ImageLoader style={photo} />
              )}
            </div>

            <NameAgeBlock
              name={profileData?.profile.name}
              age={profileData?.profile.age}
              distance={profileData.distance}
            />
            <AboutMeBlock about_me={profileData?.profile.about_me} />
            <PersonalInfoBlock profile_data={profileData.profile} />
            <LifestyleBlock profile_data={profileData.profile} />
            <LanguagesInfoBlock profile_data={profileData.profile} />
            {(relationKind === RelationKind.KIND_UNSPECIFIED ||
              relationKind === RelationKind.KIND_LIKE) && (
              <SendGiftBlock
                candidate={profileData.profile}
                relationKind={relationKind}
              />
            )}
            <div style={{ marginTop: 40 }}>
              {relationKind === RelationKind.KIND_MATCHED && (
                <UnmatchBlock
                  hid={profileData.profile.hid}
                  name={profileData.profile.name}
                />
              )}
              {relationKind !== undefined && (
                <ReportBlock
                  hid={profileData.profile.hid}
                  name={profileData.profile.name}
                  relationKind={relationKind}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const profilePhotoStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover' as const,
  borderBottomRightRadius: UIConfig.Card.Content.borderRadius
};

export default UserProfilePopup;
